import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '../../Components/UI/DataTable';
import Button from '../../Components/UI/Button';
import DeleteConfirmatationModal from '../../Components/CommonComponents/DeleteConfirmatationModal';
import UploadMedicineModal from '../../Components/CommonComponents/UploadMedicinesPopup';
import useTableData from '../../Components/CommonComponents/useTableData';
import { apiGet, apiDelete, apiPost } from '../../Components/CommonComponents/tableApi';
import { getHeadings } from './customTableFuctions';
import MedicineModal from './MedicineForm';
import { FiShoppingCart, FiAlertCircle, FiCheckSquare, FiSquare, FiDownload } from 'react-icons/fi';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import { readCart, writeCart } from './medicineCart';

const TITLES = {
  Distributors: 'Distributors',
  inventory: 'Inventory',
  medicines: 'Low Stock Medicines for Ordering',
};
const getTitle = (listType) => TITLES[listType] || 'Default Title';

// Each list keeps the exact request shape it had before.
const ENDPOINTS = {
  inventory: 'inventory/inventory/',
  medicines: 'inventory/inventory/',
  Distributors: 'inventory/distributors/',
};

const DELETE_PATHS = {
  inventory: (id) => [`inventory/inventory/${id}`, 'Medicine'],
  Distributors: (id) => [`inventory/distributors/${id}`, 'Distributor'],
};

const excelDate = (value) => (value ? String(value).slice(0, 10) : '');

const EXPORT_COLUMNS = {
  inventory: [
    { key: 'nameOfMedicine', label: 'Medicine' },
    { key: 'company', label: 'Company' },
    { key: 'potencyOrPower', label: 'Potency' },
    { key: 'typeOfMedicine', label: 'Type' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'minQuantity', label: 'Min Qty' },
    { key: 'maxQuantity', label: 'Max Qty' },
    { key: 'mrp', label: 'MRP' },
    { key: 'discount', label: 'Discount' },
    { key: 'gst', label: 'GST %' },
    { key: 'hsnCode', label: 'HSN Code' },
    { key: 'package', label: 'Package' },
    { key: 'packagingSize', label: 'Packaging Size' },
    { key: 'distributorName', label: 'Distributor' },
    { key: 'invoiceNo', label: 'Invoice No' },
    { key: 'mafDate', label: 'Mfg Date', format: excelDate },
    { key: 'expiryDate', label: 'Expiry Date', format: excelDate },
  ],
  medicines: [
    { key: 'nameOfMedicine', label: 'Medicine' },
    { key: 'company', label: 'Company' },
    { key: 'potencyOrPower', label: 'Potency' },
    { key: 'typeOfMedicine', label: 'Type' },
    { key: 'quantity', label: 'Current Qty' },
    { key: 'minQuantity', label: 'Min Qty' },
    { key: 'maxQuantity', label: 'Max Qty' },
    { key: 'distributorName', label: 'Distributor' },
  ],
  Distributors: [
    { key: 'companyName', label: 'Company Name' },
    { key: 'ownerName', label: 'Owner' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'gstNumber', label: 'GST Number' },
  ],
};

// medicines is the pick-list: rows are selected here to raise an order.
const SELECTABLE = ['medicines'];

export default function InventoryTable({ listType }) {
  const navigate = useNavigate();
  const { inventory_item_id, section } = useParams();

  const [medicineOrders, setMedicineOrders] = useState(readCart);
  const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
  const [editMedicineData, setEditMedicineData] = useState(null);
  const [uploadMedicinePopup, setuploadMedicinePopup] = useState(false);
  const [isAutoOrdering, setIsAutoOrdering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { main: tableKeys = [], display: headLabels = [] } = getHeadings(listType) || {};

  const fetcher = useCallback(
    (query) => {
      const base = ENDPOINTS[listType];
      if (!base) return [];
      const lowStockParam = listType === 'medicines' ? '&lowStockOnly=true' : '';
      return apiGet(`${base}?search=${encodeURIComponent(query.search || '')}&page=${query.page}&pageSize=${query.limit}${lowStockParam}`);
    },
    [listType]
  );

  const table = useTableData(fetcher, [listType]);
  const isSelectable = SELECTABLE.includes(listType);

  // If in 'medicines' view, strictly filter rows to only those whose quantity is <= minQuantity or <= 0
  const displayedRows = useMemo(() => {
    if (!Array.isArray(table.rows)) return [];
    if (listType !== 'medicines') return table.rows;

    return table.rows.filter((item) => {
      const min = Number(item?.minQuantity) || 0;
      const qty = Number(item?.quantity) || 0;
      return min > 0 ? qty <= min : qty <= 0;
    });
  }, [table.rows, listType]);

  // The cart outlives this screen; OrderForm empties it once the order is saved.
  useEffect(() => {
    if (isSelectable) writeCart(medicineOrders);
  }, [medicineOrders, isSelectable]);

  const toggleSelected = (itemId) =>
    setMedicineOrders((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));

  const toggleSelectAll = () => {
    if (medicineOrders.length === displayedRows.length && displayedRows.length > 0) {
      setMedicineOrders([]);
    } else {
      setMedicineOrders(displayedRows.map((r) => r?._id).filter(Boolean));
    }
  };

  // Opens the order form for every medicine at or below its minimum threshold.
  // The ids are not put in the URL: there can be thousands of them, which
  // overflows the request size limit, so the form loads the list itself.
  const generateThresholdOrder = async () => {
    setIsAutoOrdering(true);
    try {
      const lowStock = await apiGet('inventory/inventory/lowstock');
      if (!Array.isArray(lowStock) || lowStock.length === 0) {
        toast.info('No medicines are currently at or below their minimum threshold.');
        return;
      }
      navigate('/main/inventory/medicines/Order/addNew?auto=1&source=lowstock');
    } finally {
      setIsAutoOrdering(false);
    }
  };

  const orderSelected = () => navigate('/main/inventory/medicines/Order/addNew?auto=1&source=cart');

  // Every record matching the current search, not just the visible page.
  const handleExport = async () => {
    const base = ENDPOINTS[listType];
    const columns = EXPORT_COLUMNS[listType];
    if (!base || !columns) return;
    setIsExporting(true);
    try {
      const lowStockParam = listType === 'medicines' ? '&lowStockOnly=true' : '';
      const extra = listType === 'Distributors' ? '&qtype=list' : '';
      const result = await apiGet(
        `${base}?search=${encodeURIComponent(table.query.search || '')}&page=1&pageSize=100000${lowStockParam}${extra}`
      );
      const list = Array.isArray(result) ? result : result?.data || [];
      if (list.length === 0) {
        toast.info('Nothing to export');
        return;
      }
      exportToExcel(list, getTitle(listType), columns, getTitle(listType));
    } finally {
      setIsExporting(false);
    }
  };

  const headers = tableKeys.map((key, index) => ({
    key,
    label: headLabels?.[index] ?? key,
    render: (value, row) => {
      if (key === 'date') return value?.slice(0, 10);
      if (['primary', 'secondary', 'third'].includes(key)) return row?.diagnosis?.[key];
      if (key === 'quantity') {
        const min = Number(row?.minQuantity) || 0;
        const qty = Number(value) || 0;
        const isLow = (min > 0 && qty <= min) || qty <= 0;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
              isLow
                ? 'bg-rose-100 text-rose-700 border border-rose-200'
                : 'text-slate-800'
            }`}
          >
            {value ?? 0}
          </span>
        );
      }
      if (key === 'minQuantity') {
        return (
          <span className="font-semibold text-slate-700 text-xs">
            {value ?? '—'}
          </span>
        );
      }
      return value ?? 'NA';
    },
  }));

  const openRecord = (item) => {
    if (listType === 'inventory') {
      setEditMedicineData(item);
      setIsMedicineModalOpen(true);
      return;
    }
    navigate(`/main/inventory/${listType}/${listType}/${item?._id}`);
  };

  const toolbarButtons = (
    <>
      {EXPORT_COLUMNS[listType] && (
        <Button variant="outline" isLoading={isExporting} onClick={handleExport}>
          <FiDownload className="w-4 h-4" />
          Export to Excel
        </Button>
      )}
      {listType === 'inventory' && (
        <>
          <Button
            onClick={() => {
              setEditMedicineData(null);
              setIsMedicineModalOpen(true);
            }}
          >
            + Add Medicine
          </Button>
          <Button variant="secondary" onClick={() => setuploadMedicinePopup(true)}>
            Upload Medicines
          </Button>
          <DeleteConfirmatationModal
            deleteFunction={() =>
              apiDelete('inventory/inventory/delete/bulk', 'Medicines', { onDone: table.reload })
            }
            text="Medicines"
            heading="Delete All Medicines"
            name="Delete All Medicines"
          />
        </>
      )}

      {listType === 'medicines' && (
        <div className="flex items-center gap-2">
          {displayedRows.length > 0 && (
            <Button size="sm" variant="outline" onClick={toggleSelectAll}>
              {medicineOrders.length === displayedRows.length && displayedRows.length > 0 ? (
                <span className="flex items-center gap-1.5">
                  <FiCheckSquare className="w-4 h-4 text-sky-600" /> Deselect All
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <FiSquare className="w-4 h-4 text-slate-500" /> Select All ({displayedRows.length})
                </span>
              )}
            </Button>
          )}

          <Button
            variant="success"
            size="sm"
            disabled={medicineOrders.length === 0}
            onClick={orderSelected}
          >
            <FiShoppingCart className="w-4 h-4 mr-1.5" />
            Order Selected ({medicineOrders.length})
          </Button>

          <Button variant="primary" size="sm" isLoading={isAutoOrdering} onClick={generateThresholdOrder}>
            Auto Order All Low Stock
          </Button>
        </div>
      )}

      {!inventory_item_id && listType === 'Distributors' && (
        <Button variant="secondary" onClick={() => navigate(`${section}/addNew`)}>
          Add {getTitle(listType)}
        </Button>
      )}
    </>
  );

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">{getTitle(listType)}</h1>
          {listType === 'medicines' && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <FiAlertCircle className="w-3.5 h-3.5 text-amber-500" />
              Displaying only items where Current Quantity is less than or equal to Minimum Quantity.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">{toolbarButtons}</div>
      </div>

      <DataTable
        headers={headers}
        data={displayedRows}
        isLoading={table.isLoading}
        emptyMessage={
          listType === 'medicines'
            ? '🎉 Great news! No medicines are currently below their minimum quantity threshold.'
            : `No ${getTitle(listType).toLowerCase()} records found.`
        }
        page={table.query.page}
        totalPages={table.totalPages}
        rowsPerPage={table.query.limit}
        onPageChange={table.setPage}
        onRowsPerPageChange={table.setLimit}
        onSearch={table.setSearch}
        searchValue={table.query.search}
        searchPlaceholder={listType === 'medicines' ? 'Search low stock medicines...' : 'Search'}
        actionsLabel={isSelectable ? 'Order Action' : 'Actions'}
        actions={(item) =>
          isSelectable ? (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded accent-sky-600 cursor-pointer"
                checked={medicineOrders.includes(item?._id)}
                onChange={() => toggleSelected(item?._id)}
                title="Select for bulk order"
              />
              <Button
                size="xs"
                variant="primary"
                onClick={() => navigate(`/main/inventory/medicines/Order/addNew/${item?._id}?auto=1`)}
                title="Go to ordering for this medicine"
                className="whitespace-nowrap font-semibold shadow-xs"
              >
                <FiShoppingCart className="w-3 h-3 mr-1" />
                Order
              </Button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => openRecord(item)}
                className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Modify
              </button>
              <DeleteConfirmatationModal
                deleteFunction={() => {
                  const build = DELETE_PATHS[listType];
                  if (!build) return;
                  const [path, label] = build(item?._id);
                  apiDelete(path, label, { onDone: table.reload });
                }}
                text={listType}
                heading="Delete Item"
              />
            </>
          )
        }
      />

      {isSelectable && displayedRows.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-xs text-slate-600">
            <strong>{medicineOrders.length}</strong> of <strong>{displayedRows.length}</strong> low stock medicines selected
          </div>
          <Button
            variant="success"
            size="sm"
            disabled={medicineOrders.length === 0}
            onClick={orderSelected}
          >
            <FiShoppingCart className="w-4 h-4 mr-1.5" />
            Proceed to Order ({medicineOrders.length})
          </Button>
        </div>
      )}

      <UploadMedicineModal
        isOpen={uploadMedicinePopup}
        onClose={() => setuploadMedicinePopup(false)}
        onUpload={async (rows) => {
          const result = await apiPost('inventory/inventory/upload/bulk', rows, {
            loadingText: 'Uploading medicines, please wait...',
            onDone: table.reload,
          });
          if (!result) return;
          const inserted = result?.inserted ?? 0;
          const skipped = result?.skipped || [];
          if (inserted) toast.success(`${inserted} medicine${inserted === 1 ? '' : 's'} imported`);
          if (skipped.length) {
            toast.warn(`${skipped.length} row${skipped.length === 1 ? '' : 's'} skipped — row ${skipped[0].row}: ${skipped[0].reason}`);
            console.warn('Skipped inventory rows', skipped);
          }
        }}
      />

      <MedicineModal
        isOpen={isMedicineModalOpen}
        onClose={() => setIsMedicineModalOpen(false)}
        itemData={editMedicineData}
        onSuccess={table.reload}
      />

      <ToastContainer />
    </div>
  );
}
